import { useCallback, useEffect, useState, type ReactNode } from "react";
import { LogOut, RefreshCw } from "lucide-react";
import { api, sessionExpired } from "./api";
import {
  Button,
  Card,
  InlineNotice,
  Modal,
  PageHeader,
  TableFrame,
} from "./components/ui";
import { useI18n } from "./i18n";

type Device = {
  id: string;
  device_name: string;
  device_note?: string;
  user_agent: string;
  auth_source: string;
  login_ip: string;
  last_ip: string;
  created_at: string;
  expires_at: string;
  last_seen_at: string;
  current: boolean;
  online: boolean;
};
type Devices = {
  items: Device[];
  online: number;
  total: number;
  server_time: string;
};

export function AccountSessions({
  navigation,
  notesEnabled = false,
  embedded = false,
  children,
}: {
  navigation: ReactNode;
  notesEnabled?: boolean;
  embedded?: boolean;
  children?: ReactNode;
}) {
  const { t } = useI18n();
  const [data, setData] = useState<Devices>({
    items: [],
    online: 0,
    total: 0,
    server_time: "",
  });
  const [error, setError] = useState(""),
    [message, setMessage] = useState("");
  const [pending, setPending] = useState<Device | "others" | null>(null),
    [busy, setBusy] = useState(false),
    [deleteError, setDeleteError] = useState("");
  const [editing, setEditing] = useState<Device | null>(null),
    [note, setNote] = useState("");
  const load = useCallback(async () => {
    try {
      setData(await api<Devices>("/me/sessions"));
      setError("");
    } catch (e) {
      setError(String(e));
    }
  }, []);
  useEffect(() => {
    void load();
    const timer = setInterval(() => void load(), 15000);
    return () => clearInterval(timer);
  }, [load]);
  function confirm(device: Device | "others") {
    setDeleteError("");
    setMessage("");
    setPending(device);
  }
  async function revoke() {
    if (!pending || busy) return;
    setBusy(true);
    setDeleteError("");
    try {
      await api(
        pending === "others"
          ? "/me/sessions/others"
          : `/me/sessions/${encodeURIComponent(pending.id)}`,
        { method: "DELETE" },
      );
      if (pending !== "others" && pending.current) {
        sessionExpired("logout");
        return;
      }
      setPending(null);
      setMessage(t("Selected sessions have been signed out."));
      await load();
    } catch (e) {
      setDeleteError(String(e));
    } finally {
      setBusy(false);
    }
  }
  const date = (value: string) =>
    new Date(value).toLocaleString(undefined, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  const actions = (
    <>
      <Button onClick={() => void load()}>
        <RefreshCw />
        {t("Refresh")}
      </Button>
      <Button
        variant="danger"
        disabled={!data.items.some((d) => !d.current)}
        onClick={() => confirm("others")}
      >
        <LogOut />
        {t("Sign out other devices")}
      </Button>
    </>
  );
  return (
    <>
      {!embedded && (
        <PageHeader
          title={t("Login devices")}
          description={t(
            "View your active browser sessions and remotely sign out devices.",
          )}
          actions={actions}
        />
      )}
      {navigation}
      <div className="settings-content">
        <Card
          title={
            embedded
              ? t("Login devices")
              : `${t("Online")} ${data.online} · ${t("Signed in")} ${data.total}`
          }
          action={embedded ? actions : undefined}
          description={t(
            "One browser profile is one session; tabs share a session. Different browsers on the same computer appear separately.",
          )}
        >
          {embedded && (
            <div className="account-devices-heading">
              <h3>当前登录设备</h3>
              <span>
                {t("Online")} {data.online} · {t("Signed in")} {data.total}
              </span>
            </div>
          )}
          <InlineNotice>
            {t(
              "Pages send a heartbeat every 30 seconds. Sessions without activity for 2 minutes are shown as recently offline; they remain signed in until revoked or expired.",
            )}
          </InlineNotice>
          {error && <InlineNotice tone="error">{error}</InlineNotice>}
          {message && <InlineNotice tone="success">{message}</InlineNotice>}
          <TableFrame>
            <table>
              <thead>
                <tr>
                  <th>{t("Device / browser")}</th>
                  <th>{t("Status")}</th>
                  <th>{t("IP address")}</th>
                  <th>{t("Login time")}</th>
                  <th>{t("Last active")}</th>
                  <th>{t("Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((device) => (
                  <tr key={device.id}>
                    <td>
                      <strong>
                        {device.device_note || device.device_name}
                      </strong>
                      {device.device_note && (
                        <small>{device.device_name}</small>
                      )}
                      <small>
                        {device.current
                          ? t("Current device")
                          : t("Other device")}{" "}
                        ·{" "}
                        {device.auth_source === "oidc"
                          ? "OIDC"
                          : t("Local account")}
                      </small>
                      {notesEnabled && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setEditing(device);
                            setNote(device.device_note || "");
                            setDeleteError("");
                          }}
                        >
                          编辑备注
                        </Button>
                      )}
                    </td>
                    <td>
                      <span
                        className={`status-badge status-${device.online ? "success" : "neutral"}`}
                      >
                        {t(device.online ? "Online" : "Recently offline")}
                      </span>
                    </td>
                    <td>
                      <code>{device.last_ip || "—"}</code>
                      {device.login_ip !== device.last_ip && (
                        <small>
                          {t("Login IP")}: {device.login_ip}
                        </small>
                      )}
                    </td>
                    <td>
                      {date(device.created_at)}
                      <small>
                        {t("Expires")}: {date(device.expires_at)}
                      </small>
                    </td>
                    <td>{date(device.last_seen_at)}</td>
                    <td>
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={() => confirm(device)}
                      >
                        <LogOut />
                        {t(
                          device.current
                            ? "Sign out this device"
                            : "Sign out device",
                        )}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableFrame>
          {children}
        </Card>
      </div>
      <Modal
        open={pending !== null}
        title={t(
          pending === "others" ? "Sign out other devices" : "Sign out device",
        )}
        description={t(
          "The selected sessions lose access immediately. Submitted background tasks continue running.",
        )}
        onClose={() => {
          if (!busy) setPending(null);
        }}
        footer={
          <>
            <Button disabled={busy} onClick={() => setPending(null)}>
              {t("Cancel")}
            </Button>
            <Button
              variant="danger"
              disabled={busy}
              onClick={() => void revoke()}
            >
              {t(busy ? "Signing out…" : "Confirm sign out")}
            </Button>
          </>
        }
      >
        {deleteError && <InlineNotice tone="error">{deleteError}</InlineNotice>}
        {pending && pending !== "others" && (
          <p>
            <strong>{pending.device_name}</strong> · {pending.last_ip}{" "}
            {pending.current && t("Current device")}
          </p>
        )}
        {pending === "others" && (
          <p>
            {t(
              "Your current device stays signed in. All other sessions of this account will be signed out.",
            )}
          </p>
        )}
      </Modal>
      <Modal
        open={Boolean(editing)}
        title="设备备注"
        onClose={() => {
          if (!busy) setEditing(null);
        }}
        footer={
          <>
            <Button disabled={busy} onClick={() => setEditing(null)}>
              取消
            </Button>
            <Button
              disabled={busy}
              variant="primary"
              onClick={() => {
                if (!editing) return;
                setBusy(true);
                setDeleteError("");
                void api(
                  `/me/sessions/${encodeURIComponent(editing.id)}/note`,
                  { method: "PATCH", body: JSON.stringify({ note }) },
                )
                  .then(() => {
                    setEditing(null);
                    return load();
                  })
                  .catch((e) => setDeleteError(String(e)))
                  .finally(() => setBusy(false));
              }}
            >
              保存备注
            </Button>
          </>
        }
      >
        <label className="field">
          <span>备注（留空清除，最多 80 个字符）</span>
          <input value={note} onChange={(e) => setNote(e.target.value)} />
        </label>
        {deleteError && <InlineNotice tone="error">{deleteError}</InlineNotice>}
      </Modal>
    </>
  );
}
